import { dailyMemos } from "./dailyMemos.js";
import { academicYearTerms } from "./terms.js";
import type { AcademicTerm, Classroom, SchoolYear } from "./types.js";
export type YearWorkspace = {
  id: string;
  academicTerms?: AcademicTerm[];
  schoolYears?: SchoolYear[];
  currentSchoolYearId?: string;
};
export type YearRecord = { collection: string; recordId: string; data: { id: string; [key: string]: unknown } };
export type SchoolYearAction =
  | { type: "create"; startYear: number; id: string; sourceYearId?: string }
  | { type: "archive" | "reopen" | "current"; id: string }
  | { type: "delete"; id: string; confirmation: string; token: string };
// Missing year fields stay in one legacy bucket; dates and names never rewrite identity.
export function schoolYears(workspace: YearWorkspace): SchoolYear[] {
  if (workspace.schoolYears) return workspace.schoolYears;
  const names = [...new Set((workspace.academicTerms || []).map((term) => term.schoolYear))];
  return [{ id: "legacy", name: names.length === 1 ? names[0]! : "原有資料", status: "open", legacy: true }];
}
export function schoolYearStart(date: string): number {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    !Number.isFinite(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== date
  )
    throw new Error("invalid-date");
  return Number(date.slice(0, 4)) - (date.slice(5, 7) < "09" ? 1 : 0);
}
export function schoolYearForDate(workspace: YearWorkspace, date: string): SchoolYear | undefined {
  const start = schoolYearStart(date),
    years = schoolYears(workspace);
  const dated = years.find((year) => year.startDate === `${start}-09-01`);
  if (dated) return dated;
  // Unassigned legacy records stay together; their dates never rewrite ownership.
  const legacy = years.find((year) => year.legacy);
  if (
    legacy &&
    (!(workspace.academicTerms || []).length ||
      workspace.academicTerms?.some((term) => term.schoolYear === `${start}–${start + 1}`))
  )
    return legacy;
  return undefined;
}
export function ensureDateSchoolYear<W extends YearWorkspace>(workspace: W, date: string): W & YearWorkspace {
  if (!validateSchoolYears(workspace)) throw new Error("invalid-years");
  const year = schoolYearForDate(workspace, date);
  if (year) {
    if (workspace.currentSchoolYearId === year.id || (year.legacy && !workspace.schoolYears)) return workspace;
    return { ...workspace, schoolYears: schoolYears(workspace), currentSchoolYearId: year.id };
  }
  const start = schoolYearStart(date);
  const next = applySchoolYearAction(workspace, [], { type: "create", startYear: start, id: `school-year-${start}` });
  return { ...next.workspace, currentSchoolYearId: `school-year-${start}` };
}
export function schoolYearTerms(workspace: YearWorkspace, id: string): AcademicTerm[] {
  const years = schoolYears(workspace),
    year = years.find((year) => year.id === id);
  if (!year) throw new Error("unknown-year");
  const otherNames = new Set(years.filter((year) => !year.legacy).map((year) => year.name));
  return (workspace.academicTerms || []).filter((term) =>
    year.legacy ? !otherNames.has(term.schoolYear) : term.schoolYear === year.name,
  );
}
export function recordSchoolYear(workspace: YearWorkspace, records: YearRecord[], row: YearRecord): string {
  const legacy = schoolYears(workspace).find((year) => year.legacy)?.id;
  const classId = row.collection === "classes" ? row.recordId : row.data.classId;
  const classroom =
    row.collection === "classes"
      ? row
      : records.find((item) => item.collection === "classes" && item.recordId === classId);
  const explicit = row.data.schoolYearId;
  const id = classroom?.data.schoolYearId ?? explicit ?? legacy;
  if (typeof id !== "string" || !schoolYears(workspace).some((year) => year.id === id)) throw new Error("unknown-year");
  if (explicit !== undefined && explicit !== id) throw new Error("cross-year");
  return id;
}
export function scopeSchoolYear<T extends YearRecord>(workspace: YearWorkspace, records: T[], id: string): T[] {
  if (!schoolYears(workspace).some((year) => year.id === id)) throw new Error("unknown-year");
  return records.filter((row) => recordSchoolYear(workspace, records, row) === id);
}
export function validateSchoolYears(workspace: YearWorkspace): boolean {
  if (workspace.schoolYears === undefined) return workspace.currentSchoolYearId === undefined;
  if (!Array.isArray(workspace.schoolYears) || !workspace.schoolYears.length) return false;
  const ids = new Set<string>(),
    names = new Set<string>();
  let legacy = 0;
  for (const year of workspace.schoolYears) {
    if (
      !year ||
      typeof year.id !== "string" ||
      !year.id.trim() ||
      typeof year.name !== "string" ||
      !year.name.trim() ||
      !["open", "archived"].includes(year.status) ||
      ids.has(year.id) ||
      names.has(year.name)
    )
      return false;
    ids.add(year.id);
    names.add(year.name);
    if (year.legacy !== undefined && typeof year.legacy !== "boolean") return false;
    if (year.legacy === true) {
      if (year.id !== "legacy" || year.startDate !== undefined || year.endDate !== undefined) return false;
      legacy++;
    } else {
      if (year.id === "legacy" || typeof year.startDate !== "string" || typeof year.endDate !== "string") return false;
      const start = Number(year.startDate.slice(0, 4));
      if (
        !Number.isInteger(start) ||
        start < 1900 ||
        start > 9998 ||
        year.startDate !== `${start}-09-01` ||
        year.endDate !== `${start + 1}-08-31` ||
        year.name !== `${start}–${start + 1}`
      )
        return false;
    }
  }
  for (const term of workspace.academicTerms || []) {
    const owner =
      workspace.schoolYears.find((year) => !year.legacy && year.name === term.schoolYear) ||
      workspace.schoolYears.find((year) => year.legacy);
    if (!owner || (!owner.legacy && (term.startDate < owner.startDate! || term.endDate > owner.endDate!))) return false;
  }
  return legacy <= 1 && (workspace.currentSchoolYearId === undefined || ids.has(workspace.currentSchoolYearId));
}
// Exact JSON is a concurrency token, not a security checksum: no hash collisions.
export function schoolYearDeletionToken(workspace: YearWorkspace, records: YearRecord[], id: string): string {
  const year = schoolYears(workspace).find((year) => year.id === id);
  return JSON.stringify({
    year,
    terms: schoolYearTerms(workspace, id),
    records: scopeSchoolYear(workspace, records, id)
      .slice()
      .sort((a, b) => `${a.collection}\0${a.recordId}`.localeCompare(`${b.collection}\0${b.recordId}`)),
  });
}
export function applySchoolYearAction<W extends YearWorkspace, R extends YearRecord>(
  workspace: W,
  records: R[],
  action: SchoolYearAction,
  newId = () => crypto.randomUUID(),
): { workspace: W; records: YearRecord[] } {
  if (!validateSchoolYears(workspace)) throw new Error("invalid-years");
  const years = schoolYears(workspace).map((year) => ({ ...year }));
  let nextRecords: YearRecord[] = [...records],
    terms = [...(workspace.academicTerms || [])];
  let current = workspace.currentSchoolYearId || years[0]!.id;
  if (action.type === "create") {
    const start = action.startYear;
    if (!action.id.trim() || !Number.isInteger(start) || start < 1900 || start > 9998) throw new Error("invalid-year");
    const name = `${start}–${start + 1}`;
    if (
      years.some((year) => year.id === action.id || year.name === name) ||
      terms.some((term) => term.schoolYear === name)
    )
      throw new Error("duplicate-year");
    years.push({ id: action.id, name, startDate: `${start}-09-01`, endDate: `${start + 1}-08-31`, status: "open" });
    const source = action.sourceYearId
      ? schoolYears(workspace).find((year) => year.id === action.sourceYearId)
      : undefined;
    if (action.sourceYearId && !source) throw new Error("unknown-year");
    const count = source && terms.filter((term) => term.schoolYear === source.name).length === 3 ? 3 : 2;
    if (!terms.some((term) => term.schoolYear === name)) terms.push(...academicYearTerms(start, count));
    if (source) {
      const rows = scopeSchoolYear(workspace, records, source.id),
        classes = rows.filter((row) => row.collection === "classes");
      const mapping = new Map(classes.map((row) => [row.recordId, newId()])),
        now = new Date().toISOString();
      for (const row of classes) {
        const old = row.data as unknown as Classroom,
          id = mapping.get(row.recordId)!;
        const item: Classroom = {
          id,
          workspaceId: workspace.id,
          schoolYearId: action.id,
          name: old.name,
          classRole: old.classRole,
          active: old.active,
          createdAt: now,
          updatedAt: now,
          sharedDataClassId: old.sharedDataClassId ? mapping.get(old.sharedDataClassId) : undefined,
          customSubjects: old.customSubjects?.slice(),
          customDuties: old.customDuties?.slice(),
          assignmentFolders: old.assignmentFolders?.map((folder) => ({ ...folder })),
          dailyMemos: dailyMemos(old).map((memo) => ({ ...memo, studentIds: [] })),
          dutyAssignments: {},
        };
        nextRecords.push({ collection: "classes", recordId: id, data: item as unknown as YearRecord["data"] });
      }
      for (const row of rows.filter((row) => row.collection === "seatingCharts")) {
        const classId = mapping.get(String(row.data.classId)),
          id = newId();
        if (!classId) throw new Error("invalid-seating-year");
        nextRecords.push({
          collection: "seatingCharts",
          recordId: id,
          data: {
            id,
            workspaceId: workspace.id,
            classId,
            rows: row.data.rows,
            columns: row.data.columns,
            view: row.data.view,
            rowAisles: structuredClone(row.data.rowAisles),
            columnAisles: structuredClone(row.data.columnAisles),
            seats: {},
            updatedAt: now,
          },
        });
      }
    }
  } else {
    const year = years.find((year) => year.id === action.id);
    if (!year) throw new Error("unknown-year");
    if (action.type === "archive") year.status = "archived";
    if (action.type === "reopen") year.status = "open";
    if (action.type === "current") current = year.id;
    if (action.type === "delete") {
      if (
        year.status !== "archived" ||
        years.length === 1 ||
        action.confirmation !== year.name ||
        action.token !== schoolYearDeletionToken(workspace, records, year.id)
      )
        throw new Error("unsafe-delete");
      nextRecords = records.filter((row) => recordSchoolYear(workspace, records, row) !== year.id);
      const removedTerms = new Set(schoolYearTerms(workspace, year.id).map((term) => term.id));
      terms = terms.filter((term) => !removedTerms.has(term.id));
      years.splice(years.indexOf(year), 1);
      if (current === year.id) current = years.find((item) => item.status === "open")?.id || years[0]!.id;
    }
  }
  const next = { ...workspace, schoolYears: years, currentSchoolYearId: current, academicTerms: terms };
  if (!validateSchoolYears(next)) throw new Error("invalid-years");
  return { workspace: next, records: nextRecords };
}
